import { adminUrlBuilder } from '@app/api/utils';
import {
  IAvailableBundlePackResponse,
  IAvailableVouchersResponse,
  IChargeSavedCardRequest,
  IChargeSavedCardResponse,
  ICreateServicePayment,
  ICreateServicePaymentResponse,
  IIncomeSummary,
  IIncomeSummaryRequest,
  IOtherPaymentRequest, IPaymentReviewCalculationResponse,
  IPaymeBankTransfersResponse,
  IPaymeBankTransferTransactionsResponse,
  IPaymentsFilterParams,
  IPaymentsPageResponse,
  IRecentPaymentsResponse,
  IRefundPaymentRequest,
  IRefundPaymentResponse,
  PaymentRequestStatus
} from '@torim-app/types';

export interface ISavedCardStatusResponse {
  hasToken: boolean;
  paymentRequestUuid?: string;
  maxAmount?: number;
  alreadyPaid?: number;
  tokenExpiresAt?: string;
}

export class PaymentsApi {
  static path = 'payments';

  static async sendVisitPaymentLink(
    apid: string,
    body: ICreateServicePayment
  ): Promise<ICreateServicePaymentResponse> {
    const url = adminUrlBuilder({ apid }, `${PaymentsApi.path}/visit`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      if ([409, 302, 406].includes(response.status)) throw await response.json();
      else throw response.statusText;
    }
    return await response.json();
  }

  static async previewVisitPayment(
    apid: string,
    body: Omit<ICreateServicePayment, 'paymentMethod'>
  ): Promise<IPaymentReviewCalculationResponse> {
    const url = adminUrlBuilder({ apid }, `${PaymentsApi.path}/preview/visit`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    return await response.json();
  }

  static async sendVoucherPaymentLink(
    vid: string,
    body: ICreateServicePayment
  ): Promise<{ message: string | null; phoneNumber: string }> {
    const url = adminUrlBuilder({ vid }, `${PaymentsApi.path}/voucher`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      if ([409, 302, 406].includes(response.status)) throw await response.json();
      else throw response.statusText;
    }
    return await response.json();
  }

  // Payer's available credit (יתרה) for a voucher / bundle-pack / product-order purchase
  static async getPurchaseCredit(payFor: 'voucher' | 'bundle-pack' | 'product-order', id: string): Promise<{ credit: number }> {
    const url = adminUrlBuilder({ payFor, id }, `${PaymentsApi.path}/purchase-credit`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });
    if (!response.ok)
      throw 'Failed to get purchase credit';
    return await response.json();
  }

  static async sendGeneralPaymentLink(
    args: IOtherPaymentRequest
  ): Promise<{ uuid: string; }> {
    const url = adminUrlBuilder({}, `${PaymentsApi.path}/general`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(args)
    });

    if (!response.ok) {
      throw await response.json();
    }

    return await response.json();
  }

  static async sendInvoiceLink(
    apid: string
  ): Promise<{ message: string | null; phoneNumber: string }> {
    const url = adminUrlBuilder({ apid }, `${PaymentsApi.path}/invoice`);
    const response = await fetch(url, { method: 'put' });

    if (!response.ok)
      throw response.statusText;

    return await response.json();
  }

  static async getIncomeSummary(
    params: IIncomeSummaryRequest
  ): Promise<IIncomeSummary> {
    const url = adminUrlBuilder({}, `${PaymentsApi.path}/income-summary`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(params)
    });

    if (!response.ok)
      throw response.statusText;

    return await response.json();
  }

  static async refundPayment(
    body: IRefundPaymentRequest
  ): Promise<IRefundPaymentResponse> {
    const url = adminUrlBuilder({}, `${PaymentsApi.path}/refund`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw response.statusText;

    return await response.json();
  }

  static async getAvailableVouchers(
    customerId: string, serviceIds: string
  ): Promise<IAvailableVouchersResponse[]> {
    const url = adminUrlBuilder({customerId, serviceIds}, `${PaymentsApi.path}/available-vouchers`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      },
    });

    if (!response.ok)
      throw response.statusText;

    return await response.json();
  }

  static async getAvailableBundlePacks(
    customerId: string, serviceIds: string
  ): Promise<IAvailableBundlePackResponse[]> {
    const url = adminUrlBuilder({customerId, serviceIds}, `bundle-packs/available`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      },
    });

    if (!response.ok)
      throw response.statusText;

    return await response.json();
  }

  /** Paged feed of the latest payment links + payments (newest first, all statuses) */
  static async getRecentPayments(
    skip: number,
    limit: number
  ): Promise<IRecentPaymentsResponse> {
    const url = adminUrlBuilder({ skip: String(skip), limit: String(limit) }, `${PaymentsApi.path}/recent`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      },
    });

    if (!response.ok)
      throw response.statusText;

    return await response.json();
  }

  static async getSavedCardStatus(
    apid: string
  ): Promise<ISavedCardStatusResponse> {
    const url = adminUrlBuilder({ apid }, `${PaymentsApi.path}/saved-card-status`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      },
    });

    if (!response.ok)
      throw response.statusText;

    return await response.json();
  }

  /** Send an unpaid payment request to a PayMe card terminal */
  static async startPosCharge(body: { paymentRequestUuid: string; posId: string }): Promise<{ success: boolean; error?: string; pending?: boolean }> {
    const url = adminUrlBuilder({}, `${PaymentsApi.path}/pos-charge`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw await response.json();

    return await response.json();
  }

  /** Polled while the customer pays on the terminal */
  static async getPosChargeStatus(uuid: string): Promise<{ status: PaymentRequestStatus; failureReason?: string; paidPrice?: number }> {
    const url = adminUrlBuilder({ uuid }, `${PaymentsApi.path}/pos-charge-status`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      },
    });

    if (!response.ok)
      throw response.statusText;

    return await response.json();
  }

  static async chargeSavedCard(
    body: IChargeSavedCardRequest
  ): Promise<IChargeSavedCardResponse> {
    const url = adminUrlBuilder({}, `${PaymentsApi.path}/charge-saved-card`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw await response.json();

    return await response.json();
  }

  static async getLoyaltyBenefits(
    customerId: string, serviceIds: string
  ): Promise<{
    type: 'points' | 'discount';
    value: number;
  } | undefined> {
    const url = adminUrlBuilder({customerId, serviceIds}, `${PaymentsApi.path}/loyalty-benefits`);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      },
    });

    if (!response.ok)
      throw response.statusText;

    // The endpoint returns an empty body when the customer has no loyalty benefits;
    // guard against parsing an empty response as JSON.
    const text = await response.text();
    return text ? JSON.parse(text) : undefined;
  }

  static async getPaymentsPageData(
    params: IPaymentsFilterParams
  ): Promise<IPaymentsPageResponse> {
    const url = adminUrlBuilder({}, `${PaymentsApi.path}/dashboard`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(params)
    });

    if (!response.ok)
      throw response.statusText;

    return await response.json();
  }

  // PayMe withdrawals: completed transfers + funds awaiting transfer
  static async getBankTransfers(): Promise<IPaymeBankTransfersResponse> {
    const url = adminUrlBuilder({}, `${PaymentsApi.path}/bank-transfers`);
    const response = await fetch(url);
    if (!response.ok)
      throw response.statusText;
    return await response.json();
  }

  // The transactions a specific withdrawal paid out (joined with our records)
  static async getBankTransferTransactions(withdrawalDate: string): Promise<IPaymeBankTransferTransactionsResponse> {
    const url = adminUrlBuilder({date: withdrawalDate}, `${PaymentsApi.path}/bank-transfers/transactions`);
    const response = await fetch(url);
    if (!response.ok)
      throw response.statusText;
    return await response.json();
  }

  static async exportTikbulim(params: IPaymentsFilterParams): Promise<Blob> {
    return await PaymentsApi.fetchExportBlob('export/tikbulim', params);
  }

  static async exportZReport(params: IPaymentsFilterParams): Promise<Blob> {
    return await PaymentsApi.fetchExportBlob('export/z-report', params);
  }

  static async exportTransactions(params: IPaymentsFilterParams): Promise<Blob> {
    return await PaymentsApi.fetchExportBlob('export/transactions', params);
  }

  // Invoices are emailed (the zip build is slow because of ezCount rate limits), so this
  // returns immediately with the address the export will be sent to.
  static async requestInvoicesZipByEmail(params: IPaymentsFilterParams): Promise<{ queued: boolean; email: string }> {
    const url = adminUrlBuilder({}, `${PaymentsApi.path}/export/invoices-zip`);
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(params)
    });
    if (!response.ok) {
      if (response.status === 404) throw { status: 404 };
      throw response.statusText;
    }
    return await response.json();
  }

  private static async fetchExportBlob(path: string, params: IPaymentsFilterParams): Promise<Blob> {
    const url = adminUrlBuilder({}, `${PaymentsApi.path}/${path}`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(params)
    });

    if (!response.ok) {
      // Parse the body first — throwing inside the try would be swallowed by the catch
      let errorBody: unknown;
      try {
        errorBody = await response.json();
      } catch {
        throw response.statusText;
      }
      throw errorBody;
    }

    return await response.blob();
  }
}
